var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Trans } from 'react-i18next';
import { Button, message, Typography, Tabs, Checkbox, Input } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { withStore } from '../../store/rootStore';
import { Permissions } from '@whatsper/texterchat-common';
import EntityManager, { Operation } from '../EntityManager';
import EntityList from '../EntityManager/components/EntityList';
import EntityListItem from '../EntityManager/components/EntityListItem';
import ScenariosForm from './ScenariosForm/ScenariosForm';
import ScenarioRunsViewer from './ScenarioRunsViewer';
import classNames from 'classnames';
import Icon from '@ant-design/icons';
import RunDetailView from './RunDetailView/RunDetailView';
import { ReactComponent as QuickRepliesPlus } from '../../assets/icons/plus/quickRepliesPlus.svg';
import { getScenarioInfo } from './helpers';
import i18n from '../../i18n';
import styles from './ScenariosManager.module.scss';
import entityStyles from '../EntityManager/EntityManager.module.scss';
const getErrorMessage = (error) => {
    var _a, _b;
    return ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || i18n.t('unexpectedErrorTryAgain', 'An unexpected error occurred. Please try again.');
};
const ScenarioTeaser = observer(({ entity }) => {
    const { name, description } = getScenarioInfo(entity);
    return (React.createElement("div", { className: styles.entityListItemTeaser },
        React.createElement("span", { className: styles.entityListItemTeaserName }, name),
        React.createElement("span", { className: styles.entityListItemTeaserDescription }, description)));
});
class ScenariosManager extends Component {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                loading: false,
                operation: Operation.LIST,
                currentEntityId: null,
                selectedRunId: null,
                activeTab: '1',
                searchQuery: '',
                isSearchOpen: false,
            }
        });
        Object.defineProperty(this, "draftSaveTrigger", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "setDraftSaveTrigger", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (trigger) => {
                this.draftSaveTrigger = trigger;
            }
        });
        Object.defineProperty(this, "getOpSupported", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { agentsStore } = this.props.store;
                const op = [];
                if (agentsStore.hasPermissions([Permissions.Permission.scenarios_view]))
                    op.push(Operation.LIST);
                if (agentsStore.hasPermissions([Permissions.Permission.scenarios_delete]))
                    op.push(Operation.DELETE);
                if (agentsStore.hasPermissions([Permissions.Permission.scenarios_create]))
                    op.push(Operation.CREATE);
                if (agentsStore.hasPermissions([Permissions.Permission.scenarios_edit]))
                    op.push(Operation.EDIT);
                return op;
            }
        });
        Object.defineProperty(this, "renderOperationTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (op, entity) => {
                const { name } = entity ? getScenarioInfo(entity) : { name: '' };
                switch (op) {
                    case Operation.LIST:
                        return React.createElement(Trans, { i18nKey: "scenariosManager", defaults: "Scenarios" });
                    case Operation.CREATE:
                        return React.createElement(Trans, { i18nKey: "createScenario", defaults: "Create Scenario" });
                    case Operation.EDIT:
                        return React.createElement(Trans, { i18nKey: "editScenario", values: { name }, defaults: "Edit {{name}}" });
                    case Operation.DELETE:
                        return React.createElement(Trans, { i18nKey: "deleteScenario", values: { name }, defaults: "Delete {{name}}" });
                    default:
                        return React.createElement(React.Fragment, null);
                }
            }
        });
        Object.defineProperty(this, "renderDeleteQuestion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (entity) => {
                const { name } = getScenarioInfo(entity);
                return (React.createElement(Typography.Text, { className: styles.deleteQuestion },
                    React.createElement(Trans, { i18nKey: "deleteConfirmationQuestion", defaults: "Are you sure you want to delete <strong>{{name}}</strong>?", values: { name }, components: { strong: React.createElement("strong", null) } })));
            }
        });
        Object.defineProperty(this, "handleCreate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.props.history.push('/manage/scenarios');
                this.setState({
                    operation: Operation.CREATE,
                    currentEntityId: null,
                    formInitialValues: undefined,
                });
            }
        });
        Object.defineProperty(this, "handleCopyRevision", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (revision) => {
                if (!revision)
                    return;
                const initialValues = {
                    name: i18n.t('scenarioCopyName', '{{name}} (copy)', { name: revision.name || i18n.t('unnamed', 'Unnamed') }),
                    description: revision.description || '',
                };
                this.setState({
                    operation: Operation.CREATE,
                    currentEntityId: null,
                    formInitialValues: initialValues,
                });
            }
        });
        Object.defineProperty(this, "scenarioSave", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (formData, entity) => __awaiter(this, void 0, void 0, function* () {
                if (this.state.operation === Operation.EDIT && this.draftSaveTrigger) {
                    this.setState({ loading: true });
                    let success = false;
                    try {
                        yield this.draftSaveTrigger();
                        success = true;
                    }
                    catch (error) {
                        // Error message is handled inside the wizard/form
                        success = false;
                    }
                    finally {
                        this.setState({ loading: false });
                    }
                    return success;
                }
                this.setState({ loading: true });
                const { scenarios } = this.props.store;
                if ([Operation.CREATE, Operation.EDIT].includes(this.state.operation)) {
                    if (!formData.triggerEvent) {
                        message.error(React.createElement(Trans, { i18nKey: "triggerEventRequired", defaults: "A trigger event must be selected to create or edit a scenario." }));
                        this.setState({ loading: false });
                        return false;
                    }
                }
                const name = formData.name;
                let newScenarioId = null;
                let success = false;
                try {
                    if (this.state.operation === Operation.CREATE) {
                        const payload = {
                            name: formData.name,
                            description: formData.description,
                            triggerEvent: formData.triggerEvent,
                        };
                        const newScenario = yield scenarios.createScenario(payload);
                        if (newScenario) {
                            newScenarioId = newScenario._id;
                            message.success(React.createElement(Trans, { i18nKey: "scenarioCreateSuccess", values: { name } }));
                            success = true;
                        }
                    }
                    else if (this.state.operation === Operation.EDIT) {
                        // All edits are handled via the draft system which has its own save buttons.
                        // The main save button is a no-op in edit mode for scenarios.
                        success = false;
                    }
                }
                catch (error) {
                    console.error('Failed to save scenario:', error);
                    message.error(getErrorMessage(error));
                    success = false;
                }
                finally {
                    if (success && newScenarioId) {
                        yield scenarios.fetchScenarios();
                        this.props.history.push(`/manage/scenarios/${newScenarioId}`);
                        this.setState({
                            loading: false,
                            operation: Operation.EDIT,
                            currentEntityId: newScenarioId,
                        });
                    }
                    else {
                        this.setState({ loading: false });
                    }
                }
                return success;
            })
        });
        Object.defineProperty(this, "scenarioDelete", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (entity) => __awaiter(this, void 0, void 0, function* () {
                // Prevent deletion of system scenarios
                if (this.isScenarioDisabled(entity)) {
                    message.error(React.createElement(Trans, { i18nKey: "systemScenariosCannotBeDeleted", defaults: "System scenarios cannot be deleted" }));
                    this.backToList();
                    return;
                }
                this.setState({ loading: true });
                const { scenarios } = this.props.store;
                const { name } = getScenarioInfo(entity);
                try {
                    yield scenarios.deleteScenario(entity._id);
                    message.success(React.createElement(Trans, { i18nKey: "scenarioDeleteSuccess", values: { name } }));
                    this.backToList();
                }
                catch (error) {
                    console.error('Failed to delete scenario:', error);
                    message.error(getErrorMessage(error));
                }
                finally {
                    this.setState({ loading: false });
                }
            })
        });
        Object.defineProperty(this, "sortScenarios", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (a, b) => {
                const isSystemA = this.isScenarioDisabled(a);
                const isSystemB = this.isScenarioDisabled(b);
                // System scenarios always come first
                if (isSystemA && !isSystemB)
                    return -1;
                if (!isSystemA && isSystemB)
                    return 1;
                // Within same category, sort by name
                const { name: nameA } = getScenarioInfo(a);
                const { name: nameB } = getScenarioInfo(b);
                return nameA.localeCompare(nameB);
            }
        });
        Object.defineProperty(this, "isScenarioDisabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (scenario) => {
                return !!scenario.system;
            }
        });
        Object.defineProperty(this, "backToList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.props.history.push('/manage/scenarios');
                this.setState({ operation: Operation.LIST, currentEntityId: null });
            }
        });
        Object.defineProperty(this, "handleRunsConfigChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (pagination, filters, sorter) => {
                const { scenarios } = this.props.store;
                const newFilters = Object.assign({}, scenarios.runsFilters);
                if (filters.success && filters.success.length > 0) {
                    newFilters.success = filters.success[0];
                }
                else {
                    delete newFilters.success;
                }
                if (sorter.column !== undefined) {
                    delete newFilters.sortStarted;
                    delete newFilters.sortExecutionTime;
                    if (sorter.field && sorter.order) {
                        const sortField = sorter.field === 'started' ? 'sortStarted' : 'sortExecutionTime';
                        const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
                        newFilters[sortField] = sortOrder;
                    }
                }
                scenarios.setRunsConfig(pagination.page, pagination.pageSize, newFilters);
            }
        });
        Object.defineProperty(this, "handleFormFiltersChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (filters) => {
                const { scenarios } = this.props.store;
                const newFilters = Object.assign({}, scenarios.runsFilters);
                // Clear previous sort keys
                delete newFilters.sortStarted;
                delete newFilters.sortExecutionTime;
                // Set new version filter
                if (filters.version) {
                    newFilters.version = filters.version;
                }
                else {
                    delete newFilters.version;
                }
                // Set new sort order
                if (filters.sort) {
                    const [key, order] = filters.sort.split('_');
                    const sortKey = key === 'started' ? 'sortStarted' : 'sortExecutionTime';
                    newFilters[sortKey] = order;
                }
                scenarios.setRunsFilters(newFilters);
            }
        });
        Object.defineProperty(this, "handleTabChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => {
                this.setState({ activeTab: key });
            }
        });
        Object.defineProperty(this, "handleRunSelect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (runId) => {
                this.setState({ selectedRunId: runId });
            }
        });
        Object.defineProperty(this, "backToRunsList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.setState({ selectedRunId: null });
            }
        });
        Object.defineProperty(this, "handleToggleSystemScenarios", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (checked) => __awaiter(this, void 0, void 0, function* () {
                const { scenarios } = this.props.store;
                this.setState({ loading: true });
                try {
                    scenarios.setIncludeSystemScenarios(checked);
                    yield scenarios.fetchScenarios();
                    // If viewing a scenario, also refresh runs to show/hide system scenario runs
                    if (this.state.currentEntityId) {
                        yield scenarios.fetchRuns();
                    }
                }
                catch (error) {
                    console.error('Failed to fetch scenarios:', error);
                    message.error(getErrorMessage(error));
                }
                finally {
                    this.setState({ loading: false });
                }
            })
        });
        Object.defineProperty(this, "getFilteredScenarios", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { scenarios } = this.props.store;
                const { searchQuery } = this.state;
                if (!searchQuery.trim())
                    return scenarios.scenarios.slice();
                const query = searchQuery.toLowerCase();
                return scenarios.scenarios.filter((s) => {
                    const { name, description } = getScenarioInfo(s);
                    return name.toLowerCase().includes(query) || (description && description.toLowerCase().includes(query));
                });
            }
        });
    }
    componentDidMount() {
        return __awaiter(this, void 0, void 0, function* () {
            const { scenarios } = this.props.store;
            const { scenarioId } = this.props.match.params;
            this.setState({ loading: true });
            try {
                // Fetch initial UI data
                yield scenarios.fetchScenarios();
                if (scenarioId) {
                    const scenario = scenarios.scenarios.find((s) => s._id === scenarioId);
                    if (scenario) {
                        this.setState({ operation: Operation.EDIT, currentEntityId: scenarioId });
                        scenarios.setRunsFiltersForScenario(scenarioId);
                    }
                    else {
                        message.error(React.createElement(Trans, { i18nKey: "scenarioNotFound", defaults: "Scenario not found." }));
                        this.props.history.push('/manage/scenarios');
                    }
                }
                else {
                    scenarios.clearRuns();
                }
                yield Promise.all([
                    scenarios.fetchActions(),
                    scenarios.fetchEvents(),
                    scenarios.fetchConditions(),
                    scenarios.fetchLoaders(),
                ]);
            }
            catch (error) {
                console.error('Failed to load initial scenario data:', error);
                message.error(getErrorMessage(error));
            }
            finally {
                this.setState({ loading: false });
            }
        });
    }
    componentDidUpdate(prevProps, prevState) {
        const { scenarioId } = this.props.match.params;
        const prevScenarioId = prevProps.match.params.scenarioId;
        if (scenarioId !== prevScenarioId) {
            if (scenarioId) {
                this.props.store.scenarios.setRunsFiltersForScenario(scenarioId);
                // If a scenarioId appears while we are in CREATE mode (e.g., after import),
                // switch to EDIT mode and focus that scenario immediately.
                if (this.state.operation === Operation.CREATE) {
                    this.setState({ operation: Operation.EDIT, currentEntityId: scenarioId });
                }
            }
            else {
                this.props.store.scenarios.clearRuns();
                if ([Operation.EDIT, Operation.DELETE].includes(this.state.operation)) {
                    this.setState({ operation: Operation.LIST, currentEntityId: null });
                }
            }
            if (this.state.selectedRunId) {
                this.setState({ selectedRunId: null });
            }
        }
        // Prevent delete operation for system scenarios
        if (this.state.operation === Operation.DELETE &&
            this.state.operation !== prevState.operation &&
            this.state.currentEntityId) {
            const scenario = this.props.store.scenarios.scenarios.find((s) => s._id === this.state.currentEntityId);
            if (scenario && this.isScenarioDisabled(scenario)) {
                message.error(React.createElement(Trans, { i18nKey: "systemScenariosCannotBeDeleted", defaults: "System scenarios cannot be deleted" }));
                this.backToList();
            }
        }
    }
    render() {
        const { scenarios, agentsStore } = this.props.store;
        const { operation, currentEntityId, loading, selectedRunId, activeTab } = this.state;
        const opSupported = this.getOpSupported();
        const getInitialSort = () => {
            const { runsFilters } = scenarios;
            if (runsFilters.sortStarted)
                return `started_${runsFilters.sortStarted}`;
            if (runsFilters.sortExecutionTime)
                return `executionTime_${runsFilters.sortExecutionTime}`;
            return undefined;
        };
        const currentEntity = currentEntityId ? scenarios.scenarios.find((s) => s._id === currentEntityId) : null;
        const formProps = {
            onCopyRevision: this.handleCopyRevision,
            initialValues: this.state.formInitialValues,
            setDraftSaveTrigger: this.setDraftSaveTrigger,
            scenario: currentEntity,
            runs: scenarios.runs.slice(),
            agentsStore,
            entity: currentEntity,
            onSave: this.scenarioSave,
            onCancel: this.backToList,
            loading: loading,
            isSystemScenario: currentEntity ? this.isScenarioDisabled(currentEntity) : false,
        };
        const scenarioDetailComponent = (props) => {
            if (props.entity && operation === Operation.EDIT) {
                const isSystemScenario = currentEntity ? this.isScenarioDisabled(currentEntity) : false;
                return (React.createElement(Tabs, { activeKey: activeTab, onChange: this.handleTabChange, className: styles.scenarioTabs },
                    React.createElement(Tabs.TabPane, { tab: isSystemScenario ?
                            React.createElement(Trans, { i18nKey: "scenarioDetailsReadOnly", defaults: "Details (Read-Only)" }) :
                            React.createElement(Trans, { i18nKey: "editDraftTab", defaults: "Edit Draft" }), key: "1" },
                        React.createElement(ScenariosForm, Object.assign({}, props))),
                    React.createElement(Tabs.TabPane, { tab: React.createElement(Trans, { i18nKey: "pastRunsTab", defaults: "Past Runs" }), key: "2" }, selectedRunId ? (React.createElement(RunDetailView, { run: scenarios.runs.find(r => r._id === selectedRunId), onBack: this.backToRunsList })) : (React.createElement(ScenarioRunsViewer, { runs: scenarios.runs.slice(), loading: scenarios.loading, totalRuns: scenarios.totalRuns, currentPage: scenarios.runsPage, pageSize: scenarios.runsPageSize, onRunSelect: this.handleRunSelect, onConfigChange: this.handleRunsConfigChange, onFiltersChange: this.handleFormFiltersChange, initialFilters: { version: scenarios.runsFilters.version, sort: getInitialSort() } })))));
            }
            return React.createElement(ScenariosForm, Object.assign({}, props));
        };
        const isDetailsActiveMobile = operation === Operation.CREATE || operation === Operation.EDIT || operation === Operation.DELETE;
        const listTitle = this.renderOperationTitle(Operation.LIST);
        return (React.createElement("div", { className: classNames(entityStyles.entityManagerLayout, { [entityStyles.detailsVisibleMobile]: isDetailsActiveMobile }) },
            React.createElement("div", { className: entityStyles.sidebar },
                React.createElement("div", { className: entityStyles.sidebarListHeader },
                    React.createElement("span", null, listTitle),
                    React.createElement("div", { className: styles.headerButtons },
                        React.createElement(Button, { type: "text", icon: this.state.isSearchOpen ? React.createElement(CloseOutlined, null) : React.createElement(SearchOutlined, null), onClick: () => {
                                if (this.state.isSearchOpen) {
                                    this.setState({ searchQuery: '' });
                                }
                                this.setState((prev) => ({ isSearchOpen: !prev.isSearchOpen }));
                            }, className: entityStyles.actionButton }),
                        opSupported.includes(Operation.CREATE) && (React.createElement(Button, { type: "text", icon: React.createElement(Icon, { component: QuickRepliesPlus }), onClick: this.handleCreate, className: entityStyles.actionButton })))),
                this.state.isSearchOpen && (React.createElement("div", { className: styles.searchContainer },
                    React.createElement(Input, { placeholder: i18n.t('searchPlaceholder'), prefix: React.createElement(SearchOutlined, null), value: this.state.searchQuery, onChange: (e) => this.setState({ searchQuery: e.target.value }), allowClear: true, autoFocus: true }))),
                React.createElement("div", { className: styles.filterBar },
                    React.createElement(Checkbox, { checked: scenarios.includeSystemScenarios, onChange: (e) => this.handleToggleSystemScenarios(e.target.checked) },
                        React.createElement(Trans, { i18nKey: "includeSystemScenarios", defaults: "Include system scenarios" }))),
                React.createElement(EntityList, { entities: this.getFilteredScenarios(), loading: loading, teaser: ScenarioTeaser, additionalProps: {}, entityIdKey: "_id", createTitle: this.renderOperationTitle(Operation.CREATE), onCreate: opSupported.includes(Operation.CREATE)
                        ? this.handleCreate
                        : undefined, onEdit: opSupported.includes(Operation.EDIT)
                        ? (entity) => {
                            this.props.history.push(`/manage/scenarios/${entity._id}`);
                            this.setState({ operation: Operation.EDIT, currentEntityId: entity._id });
                        }
                        : undefined, onDelete: opSupported.includes(Operation.DELETE)
                        ? (entity) => {
                            // System scenarios cannot be deleted - this is caught in componentDidUpdate
                            this.props.history.push(`/manage/scenarios/${entity._id}`);
                            this.setState({ operation: Operation.DELETE, currentEntityId: entity._id });
                        }
                        : undefined, renderListItem: (entity, props) => {
                        const isSystemScenario = this.isScenarioDisabled(entity);
                        const Teaser = props.teaser;
                        const sortedEntities = [...props.entities].sort(props.sorter || (() => 0));
                        const entityIndex = sortedEntities.findIndex((e) => e._id === entity._id);
                        // Determine if we need a section header
                        let sectionHeader = null;
                        if (entityIndex === 0 && isSystemScenario) {
                            sectionHeader = (React.createElement("li", { key: "section-system", className: styles.sectionHeader },
                                React.createElement(Trans, { i18nKey: "systemScenariosSection", defaults: "System Scenarios" })));
                        }
                        else if (entityIndex > 0) {
                            const prevEntity = sortedEntities[entityIndex - 1];
                            const prevIsSystem = this.isScenarioDisabled(prevEntity);
                            if (prevIsSystem && !isSystemScenario) {
                                sectionHeader = (React.createElement("li", { key: "section-regular", className: styles.sectionHeader },
                                    React.createElement(Trans, { i18nKey: "regularScenariosSection", defaults: "Scenarios" })));
                            }
                        }
                        if (entityIndex === 0 && !isSystemScenario) {
                            // No system scenarios exist, show regular header
                            sectionHeader = (React.createElement("li", { key: "section-regular", className: styles.sectionHeader },
                                React.createElement(Trans, { i18nKey: "regularScenariosSection", defaults: "Scenarios" })));
                        }
                        return (React.createElement(React.Fragment, { key: entity._id },
                            sectionHeader,
                            React.createElement(EntityListItem, { entity: entity, entityIdKey: "_id", onEdit: props.onEdit, onDelete: isSystemScenario ? undefined : props.onDelete, customListActions: props.customListActions, isHighlighted: props.currentOperation === Operation.EDIT &&
                                    props.selectedEntityId !== null &&
                                    props.selectedEntityId !== undefined &&
                                    entity._id === props.selectedEntityId },
                                React.createElement(Teaser, Object.assign({ entity: entity }, props.additionalProps)))));
                    }, sorter: this.sortScenarios, currentOperation: operation, selectedEntityId: operation === Operation.EDIT && currentEntity ? currentEntity._id : null })),
            React.createElement("div", { className: entityStyles.contentArea },
                React.createElement(EntityManager, { key: currentEntityId || 'create', entities: scenarios.scenarios.slice(), entityIdKey: "_id", teaser: ScenarioTeaser, form: scenarioDetailComponent, formProps: formProps, renderOperationTitle: this.renderOperationTitle, renderDeleteQuestion: this.renderDeleteQuestion, opSupported: opSupported, onEntitySave: this.scenarioSave, onEntityDelete: this.scenarioDelete, loading: loading, sorter: this.sortScenarios, operationProp: operation, currentEntityProp: currentEntity || undefined, onDeleteCompleted: this.backToList, onCancelForm: this.backToList, onMobileBack: this.backToList }))));
    }
}
export default withStore(withRouter(observer(ScenariosManager)));
